import {skipToken} from '@reduxjs/toolkit/query';

import {isNotNil} from 'ramda';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {useGetTelephonyCallQuery} from '@dms/api/settings';

import {useCallPanel} from './useCallPanel';
import {useCurrentTelephonyCall} from './useCurrentTelephonyCall';
import {usePairableCall} from './usePairableCall';
import {useTelephonyCallAvailability} from './useTelephonyCallAvailability';

/** What the call panel shows, and whether it shows at all. */
export function useCallPanelContent() {
  const {state: panel} = useCallPanel();
  const {isAllowed} = useTelephonyCallAvailability();
  const {call: liveCall} = useCurrentTelephonyCall();
  const {state: pairable} = usePairableCall();
  const {data: currentUser} = useGetCurrentUserInfoQuery();

  // The layout reads this for every user: without telephony a stored call shows nothing.
  const pairableCallId =
    isAllowed && isNotNil(pairable) && !pairable.isDismissed && pairable.userId === currentUser?.id
      ? pairable.callId
      : null;
  // Shares the pairing part's cache entry. Its re-read can see the end before the live state does
  // (a lost push); the live part then hides, so the caller never shows twice.
  const {data: pairableCall} = useGetTelephonyCallQuery(
    isNotNil(pairableCallId) ? {callId: pairableCallId} : skipToken
  );
  const isAfterCall = pairableCall?.status === 'ended';
  const isLivePartHidden = isAfterCall && liveCall?.id === pairableCall?.id;
  // Another call ringing or starting: the previous call's after-call part waits, so the panel shows
  // one caller. It comes back if the new call is not taken; taking it replaces the previous call.
  const isOtherCallLive =
    isNotNil(liveCall) && liveCall.id !== pairableCallId && liveCall.status !== 'ended';
  const shownPairableCallId = isAfterCall && isOtherCallLive ? null : pairableCallId;

  return {
    isShown: panel.isOpen && (isNotNil(liveCall) || isNotNil(shownPairableCallId)),
    isAfterCall,
    isLivePartHidden,
    shownPairableCallId,
  };
}
